import Dropdown from "../Dropdown.jsx";
import {useContext, useEffect, useState} from "react";
import {GlobalContext} from "../../utils/context/GlobalContextProvider.jsx";
import {useTranslation} from "react-i18next";
import {useErrorTranslation} from "../../api/utilities/translateErrorMessage.js";
import toast from "react-hot-toast";
import {ProjectApi, SystemApi} from "../../api/authority/generated/index";
import moment from "moment-timezone";
import Spinner, {SpinnerSize} from "../Spinner.jsx";
import {faCheck, faExclamationTriangle, faPlay} from "@fortawesome/free-solid-svg-icons";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faRefresh} from "@fortawesome/free-solid-svg-icons/faRefresh";
import {isPreview} from "../../utils/environment.js";
import Button from "../Button.jsx";
import {useIsAdministrator} from "../auth/rolesAndPermissions.js";

export default function UpdateDropdown() {
    let {context, getAuthorityConfiguration} = useContext(GlobalContext);

    let {t} = useTranslation();
    let {et} = useErrorTranslation();

    let [data, setData] = useState(null);
    let [noProject, setNoProject] = useState(false);
    let [demoLoaded, setDemoLoaded] = useState(false);

    let [refreshState, setRefreshState] = useState(0);

    let isAdministrator = useIsAdministrator();

    useEffect(() => {
        setNoProject(false);
        let found = false;

        if (isPreview()) {
            setDemoLoaded(true);
            setLastUpdateDateTime(moment());
            setData({});
            return;
        }

        setData(null);

        for (let possibleProject of Object.values(context.currentUserProjects)) {
            if (possibleProject.id === context.currentProjectId) {
                found = true;
                new ProjectApi(getAuthorityConfiguration())
                    .projectStatusRaw({
                        projectId: possibleProject.id,
                    })
                    .then(response => response.raw.json())
                    .then(jsonData => {
                        setData(jsonData);
                    })
                    .catch((e) => et(e).then(toast.error));
            }
        }

        if (!found) {
            setData(null);
            setNoProject(true);
        }

        // refresh the values after a minute
        const timeoutId = setTimeout(() => {
            setData(null);
            setRefreshState((refreshState) => refreshState + 1);
        }, 1000 * 60);

        return () => {
            clearTimeout(timeoutId);
        };
    }, [context.currentProjectId, context.currentUserProjectsLoaded, refreshState]);


    function queueIntegration(integrationId) {
        new SystemApi(getAuthorityConfiguration())
            .enqueueIntegration({
                integrationId: integrationId,
            })
            .then(response => {
                setRefreshState((refreshState) => refreshState + 1);
            })
            .catch((e) => et(e).then(toast.error));
    }

    function queueProjectPrecalculation(projectId) {
        new SystemApi(getAuthorityConfiguration())
            .enqueueProjectPrecalculation({
                projectId: projectId,
            })
            .then(response => {
                setRefreshState((refreshState) => refreshState + 1);
            })
            .catch((e) => et(e).then(toast.error));
    }

    let [lastUpdateDateTime, setLastUpdateDateTime] = useState(null);

    let [hasError, setHasError] = useState(false);
    let [hasUnfinishedIntegration, setHasUnfinishedIntegration] = useState(false);

    useEffect(() => {
            if (isPreview()) {
                return;
            }

            let lastUpdateTime = null;
            setHasError(false);
            for (let integrationStatus of Object.values(data?.integrationStatuses ?? {})) {
                let lastSuccessfulUpdateTime = moment(integrationStatus.lastSuccessfulUpdate);
                if (lastUpdateTime === null || lastSuccessfulUpdateTime.isBefore(lastUpdateTime)) {
                    lastUpdateTime = lastSuccessfulUpdateTime;
                }

                if (integrationStatus.status === 'Failed') {
                    setHasError(true);
                }
            }

            setLastUpdateDateTime(lastUpdateTime);
        },
        [data]
    );

    let iconColor = 'text-green-800 dark:text-green-500';
    let icon = faCheck;

    // Has error, make it yellow so users notice!
    if (hasUnfinishedIntegration || hasError) {
        icon = faExclamationTriangle;
        iconColor = 'text-red-500';
    }

    let nameByStatus = {
        'Scheduled': t('integration.status.scheduled'),
        'Succeeded': t('integration.status.finished'),
        'Failed': t('integration.status.error'),
        'Running': t('integration.status.running'),
    };

    return <Dropdown
        align={'right'}
        title={(data === null || noProject || lastUpdateDateTime === null) ?
            <Spinner size={SpinnerSize["spinner-small"]}/>
            : <div className={''}>
                <FontAwesomeIcon icon={icon} className={iconColor}/>
            </div>
        }
        showTitle={false}
    >
        {noProject && <div>{t('project.awaiting-project')}</div>}
        {demoLoaded && <div>{t('project.demo-data-loaded')}</div>}

        {data !== null && data.integrationStatuses?.map((integration) => {

            let header = integration.integrationName + ' - ' + nameByStatus[integration.status];

            let icon = faCheck;
            let iconColors = 'bg-green-500 text-white';
            let textColors = 'text-green-500';

            if (integration.status === 'Scheduled') {
                icon = faRefresh;
                iconColors = 'bg-blue-500 text-white';
                textColors = 'text-blue-500';
            }

            if (integration.status === 'Failed') {
                icon = faExclamationTriangle;
                iconColors = 'bg-red-500 text-white';
                textColors = 'text-red-500';
            }

            if (integration.status === 'Running') {
                icon = faRefresh;
                iconColors = 'bg-cyan-700 text-cyan-100';
                textColors = 'text-cyan-500';
            }

            return <div key={'status-' + integration.integrationId} className={'w-96 mb-1'}>
                <div className="flex w-full max-w-sm overflow-hidden rounded-lg shadow-md bg-gray-100 dark:bg-gray-700">
                    <div className={"flex items-center justify-center w-12 " + iconColors}>
                        <FontAwesomeIcon icon={icon}/>
                    </div>

                    <div className="mx-3 mb-1 w-full">
                            <span className={"font-semibold text-sm " + textColors}>
                                {integration.integrationId === context.currentProjectId
                                    ? t('project.pre-calculation-status')
                                    : header
                                }
                            </span>
                        {integration.lastSuccessfulUpdate !== null
                            && (integration.status !== 'Running' || integration.integrationId === context.currentProjectId)
                            && <p className="text-xs text-gray-600 dark:text-gray-200">
                                {t('project.last-refresh')}: {moment(integration.lastSuccessfulUpdate).format('DD.MM.YYYY HH:mm')}
                            </p>
                        }
                        {integration.nextUpdate !== null &&
                            <p className="text-xs text-gray-600 dark:text-gray-200">

                                {t('project.next-refresh')}: {moment(integration.nextUpdate).format('DD.MM.YYYY HH:mm')}
                            </p>
                        }
                        {
                            integration.status === 'Failed' &&
                            <div className={'font-mono form-textarea box-border w-full ' + textColors}>
                                {integration.error}
                            </div>
                        }
                    </div>

                    {
                        isAdministrator &&
                        (integration.status === 'Failed' || integration.status === 'Succeeded') &&
                        <Button
                            variant={'outline-violet'}
                            onClick={() => {
                                if(integration.integrationId === context.currentProjectId) {
                                    queueProjectPrecalculation(context.currentProjectId);
                                } else {
                                    queueIntegration(integration.integrationId);
                                }
                            }}
                        >
                            <FontAwesomeIcon icon={faPlay}/>
                        </Button>
                    }

                </div>
                {integration.integrationId === context.currentProjectId &&
                    <div className={'border-b-1 mt-1.5 mb-1.5'}></div>}
            </div>;
        })}
    </Dropdown>
}